import type { HippoNavigationItem } from '@hippo/components/navigation/navigation.types';

export interface ModuleSearchPart {
    text: string;
    match: boolean;
}

export interface ModuleSearchResult {
    item: HippoNavigationItem;
    ancestors: HippoNavigationItem[];
    groupTitle: string;
    parts: ModuleSearchPart[];
}

interface Folded {
    chars: string[];
    /** Index of the original code point each folded character came from. */
    map: number[];
}

/**
 * Case-, accent- and ß-insensitive form of a string. Combining marks are
 * dropped whether the text arrived precomposed or not, and every folded
 * character remembers the code point it came from so a match can be
 * highlighted in the title as the user typed it.
 */
const fold = (value: string): Folded => {
    const chars: string[] = [];
    const map: number[] = [];
    Array.from(value).forEach((ch, index) => {
        const base = ch.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
        for (const folded of Array.from(base === 'ß' ? 'ss' : base)) {
            chars.push(folded);
            map.push(index);
        }
    });
    return { chars, map };
};

const isWordStart = (chars: string[], index: number): boolean =>
    index === 0 || !/[\p{L}\p{N}]/u.test(chars[index - 1] ?? '');

const contains = (haystack: string[], needle: string[]): boolean => {
    for (let i = 0; i + needle.length <= haystack.length; i++) {
        if (needle.every((ch, j) => haystack[i + j] === ch)) {
            return true;
        }
    }
    return false;
};

/** Best occurrence of the needle: 0 = title starts with it, 1 = a word does, 2 = inside a word. */
const bestMatch = (
    haystack: string[],
    needle: string[]
): { index: number; score: number } | null => {
    let best: { index: number; score: number } | null = null;
    for (let i = 0; i + needle.length <= haystack.length; i++) {
        if (!needle.every((ch, j) => haystack[i + j] === ch)) {
            continue;
        }
        const score = i === 0 ? 0 : isWordStart(haystack, i) ? 1 : 2;
        if (!best || score < best.score) {
            best = { index: i, score };
        }
        if (score === 0) {
            break;
        }
    }
    return best;
};

const splitParts = (
    title: string,
    folded: Folded,
    start: number,
    length: number
): ModuleSearchPart[] => {
    const from = folded.map[start];
    const last = folded.map[start + length - 1];
    if (from === undefined || last === undefined) {
        return [{ text: title, match: false }];
    }
    const chars = Array.from(title);
    return [
        { text: chars.slice(0, from).join(''), match: false },
        { text: chars.slice(from, last + 1).join(''), match: true },
        { text: chars.slice(last + 1).join(''), match: false },
    ].filter(part => part.text);
};

/**
 * Every openable module whose title or section matches the query, best
 * match first: title starts with the query, then a word of the title does,
 * then the query appears inside a word, then every word of the query
 * appears somewhere in title + section. Items are visited in menu order,
 * so ties keep the order the user knows from the sidebar.
 */
export function searchNavigation(
    navigation: HippoNavigationItem[],
    query: string,
    isVisible: (item: HippoNavigationItem) => boolean
): ModuleSearchResult[] {
    const needle = fold(query.trim()).chars;
    if (!needle.length) {
        return [];
    }
    const words = query
        .trim()
        .split(/\s+/)
        .map(word => fold(word).chars);

    const scored: { result: ModuleSearchResult; score: number }[] = [];

    const visit = (
        items: HippoNavigationItem[],
        ancestors: HippoNavigationItem[]
    ): void => {
        for (const item of items) {
            if (!isVisible(item)) {
                continue;
            }
            if ((item.link || item.function) && !item.disabled) {
                const title = item.title ?? '';
                const groupTitle = ancestors[0]?.title ?? '';
                const foldedTitle = fold(title);
                const match = bestMatch(foldedTitle.chars, needle);

                let score: number | null = match?.score ?? null;
                if (score === null) {
                    const context = fold(`${title} ${groupTitle}`).chars;
                    if (words.every(word => contains(context, word))) {
                        score = 3;
                    }
                }

                if (score !== null) {
                    scored.push({
                        score,
                        result: {
                            item,
                            ancestors,
                            groupTitle,
                            parts: match
                                ? splitParts(
                                      title,
                                      foldedTitle,
                                      match.index,
                                      needle.length
                                  )
                                : [{ text: title, match: false }],
                        },
                    });
                }
            }
            if (item.children?.length) {
                visit(item.children, [...ancestors, item]);
            }
        }
    };
    visit(navigation, []);

    return scored
        .map((entry, order) => ({ ...entry, order }))
        .sort((a, b) => a.score - b.score || a.order - b.order)
        .map(entry => entry.result);
}
